/**
 * Cálculos puros de balance compartidos entre `balanceConsolidadoService` y sus tests.
 *
 * Se extrajeron de los métodos del servicio para eliminar la duplicación del patrón
 * `filter(tipo) + reduce(Number(monto))` que se repetía en cada método de consolidación,
 * y para que los tests verifiquen el código real de producción en lugar de una copia local.
 */

export interface MovimientoMonto {
  readonly tipo: string;
  readonly monto: string | number;
  readonly origen_tipo?: string | null;
}

export interface TotalesBalance {
  readonly ingresos: number;
  readonly egresos: number;
  /** Entradas por financiamiento de deudas: fuera de los ingresos, dentro del saldo. */
  readonly financiamiento: number;
  readonly balance: number;
}

/** Entrada por financiamiento: dinero prestado que entró a la cuenta. */
export function esFinanciamiento(m: Pick<MovimientoMonto, 'tipo' | 'origen_tipo'>): boolean {
  return m.tipo === 'ingreso' && m.origen_tipo === 'financiamiento';
}

/** Ingreso propio: lo que cuenta en "Ingresos Totales". */
export function esIngresoPropio(m: Pick<MovimientoMonto, 'tipo' | 'origen_tipo'>): boolean {
  return m.tipo === 'ingreso' && m.origen_tipo !== 'financiamiento';
}

function sumar(movimientos: readonly MovimientoMonto[], criterio: (m: MovimientoMonto) => boolean): number {
  return movimientos.filter(criterio).reduce((suma, m) => suma + Number(m.monto), 0);
}

/**
 * Suma ingresos propios, financiamiento y egresos de una lista de movimientos y calcula el balance neto.
 * El financiamiento no es un ingreso propio, pero sí suma al saldo.
 *
 * No filtra por `afecta_balance`: ese filtro ocurre en la query SQL antes de invocar
 * esta función. Quien llama es responsable de pasar solo los movimientos que correspondan.
 */
export function calcularTotales(movimientos: readonly MovimientoMonto[]): TotalesBalance {
  const ingresos = sumar(movimientos, esIngresoPropio);
  const financiamiento = sumar(movimientos, esFinanciamiento);
  const egresos = sumar(movimientos, (m) => m.tipo === 'egreso');
  return { ingresos, egresos, financiamiento, balance: ingresos + financiamiento - egresos };
}

/**
 * Descarta el dinero en tránsito: lo que la entidad recauda para transferir hacia arriba
 * (cuota iglesia) y las donaciones. No es ingreso ni gasto propio.
 *
 * **Se aplica por igual a ingresos y egresos.** La asimetría entre ambos lados fue un bug real:
 * el ingreso filtraba, el egreso no, y el balance neto salía por debajo del real en toda pantalla
 * que lo mostrara. Cualquier cálculo de balance que filtre un lado debe filtrar el otro con esta
 * misma función.
 */
export function filtrarQueAfectanBalance<T extends { afecta_balance?: boolean | null }>(
  movimientos: readonly T[]
): T[] {
  return movimientos.filter(m => m.afecta_balance !== false);
}

/**
 * Consolida los totales de múltiples entidades en un único total agregado.
 */
export function consolidarTotales(
  entidades: readonly Pick<TotalesBalance, 'ingresos' | 'egresos' | 'financiamiento'>[]
): TotalesBalance {
  const ingresos = entidades.reduce((sum, e) => sum + e.ingresos, 0);
  const egresos = entidades.reduce((sum, e) => sum + e.egresos, 0);
  const financiamiento = entidades.reduce((sum, e) => sum + e.financiamiento, 0);
  return { ingresos, egresos, financiamiento, balance: ingresos + financiamiento - egresos };
}
